import React from 'react';
import { useParams, useSearchParams, Link, Navigate } from 'react-router-dom';
import { Loader2, ArrowLeft, AlertCircle, BarChart3, Download, Filter, CalendarClock } from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import SignInModal from '../../components/self-service/auth/SignInModal';
import { usePublisherConfig } from '../../data/publisherConfigs';
import { apiClient } from '../../lib/apiClient';
import type { SelfServiceSubmissionRow } from '../../types/selfService';
import { bucketFor, hasDeliveredData, SharedKeyframes } from '../../components/self-service/campaignSharedHelpers';
import PublicHeader from '../../components/self-service/PublicHeader';
import { aggregateMonthly } from '../../components/self-service/deliveryReportHelpers';
import { bucketByDaypart, shiftHoursToLocal } from '../../components/self-service/dayparts';
import { computeZipMapFrame, zipBasemapUrl as zipBasemapUrlBuilder } from '../../components/self-service/shared/zipMapFrame';
import GeographicSection from '../../components/self-service/GeographicSection';
import DaypartChart from '../../components/self-service/DaypartChart';
import ByCreativeSection from '../../components/self-service/ByCreativeSection';
import InsightsBand from '../../components/self-service/InsightsBand';
import CampaignStatsBand from '../../components/self-service/CampaignStatsBand';
import PerformanceTrends from '../../components/self-service/PerformanceTrends';
import DateRangeFilter from '../../components/self-service/DateRangeFilter';
import { rangeQuery, rangeLabel, minISO, todayISO, addDaysISO, type ReportRange } from '../../components/self-service/reportRange';
import Banner from '../../components/self-service/shared/Banner';
import { formatDate } from '../../components/self-service/shared/dateFormat';
import { exportDeliveryReportToPdf, deliveryReportFilenameFor } from '../../services/deliveryReportPdf';
import { REPORT_ACCENT } from '../../components/self-service/reportTheme';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';

// Shapes for the extra report endpoints we pull for the PDF export.
interface GeoData {
  total: { impressions: number; completes: number };
  byState: Array<{ state: string; impressions: number; completes: number }>;
  byDma: Array<{ dma: string; name?: string | null; impressions: number; completes: number }>;
  byZip: Array<{ zip: string; impressions: number; completes: number }>;
}
interface DaypartData {
  byHour: Array<{ hour: number; impressions: number; completes: number }>;
  offsetMinutes?: number | null;
  timezoneLabel?: string | null;
}
interface CreativeData {
  name: string;
  impressions: number;
  completes: number;
}

// Advertiser branding, cached in sessionStorage per submission so a refresh
// re-applies the brand color immediately instead of flashing the default green
// for one branding round-trip. Revalidated on every mount.
interface Branding {
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
}
const EMPTY_BRANDING: Branding = { logoUrl: null, primaryColor: null, secondaryColor: null };
const BRANDING_CACHE_PREFIX = 'ss-report-branding:';

function readCachedBranding(submissionId: string | undefined): Branding {
  if (!submissionId) return EMPTY_BRANDING;
  try {
    const raw = sessionStorage.getItem(BRANDING_CACHE_PREFIX + submissionId);
    if (!raw) return EMPTY_BRANDING;
    const p = JSON.parse(raw) as Partial<Branding>;
    return {
      logoUrl: typeof p.logoUrl === 'string' ? p.logoUrl : null,
      primaryColor: typeof p.primaryColor === 'string' ? p.primaryColor : null,
      secondaryColor: typeof p.secondaryColor === 'string' ? p.secondaryColor : null,
    };
  } catch {
    return EMPTY_BRANDING;
  }
}

// Format a UTC ISO timestamp as e.g. "Jun 8, 2026, 12:04 PM UTC" for the
// "Last updated" stamp. Returns null for a missing/invalid value.
function formatUtcStamp(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return (
    d.toLocaleString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }) + ' UTC'
  );
}

function writeCachedBranding(submissionId: string | undefined, b: Branding): void {
  if (!submissionId) return;
  try {
    sessionStorage.setItem(BRANDING_CACHE_PREFIX + submissionId, JSON.stringify(b));
  } catch {
    // sessionStorage unavailable / quota exceeded — caching is best-effort.
  }
}

// CTV-445: dedicated performance-report page reached from the campaign detail
// page. Hosts the delivery report + geographic breakdown (and, as later slices
// land, daypart + by-creative). Loads its own submission row so a direct URL or
// refresh works — it does not depend on navigation state.
const CampaignReportPage: React.FC = () => {
  const { slug, submissionId } = useParams<{ slug: string; submissionId: string }>();
  // ?refreshBranding=1 on the report URL forces the advertiser branding to
  // re-derive (bypasses the API's cache) — handy when re-testing a website.
  const [searchParams] = useSearchParams();
  const wantsFreshBranding = !!searchParams.get('refreshBranding');
  const { user, isAuthenticated, selectedAdvertiser } = useAuth();
  const [signInOpen, setSignInOpen] = React.useState(!isAuthenticated);
  const [row, setRow] = React.useState<SelfServiceSubmissionRow | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  // CTV-445: delivery data for the page-level "Download PDF" action. Fetched
  // here (not just inside the delivery card) so the export lives at the top of
  // the page. The PDF contents are slated to expand beyond delivery.
  const [pdfDaily, setPdfDaily] = React.useState<Array<{ date: string; plays: number; completes?: number }>>([]);
  const [pdfFlights, setPdfFlights] = React.useState<Array<{ pacing_impressions: number }>>([]);
  // CTV-445: the rest of the report's sections, fetched for the PDF export so it
  // mirrors the on-page report (geographic, daypart, by-creative, completion).
  const [pdfGeo, setPdfGeo] = React.useState<GeoData | null>(null);
  const [pdfDaypart, setPdfDaypart] = React.useState<DaypartData | null>(null);
  const [pdfCreatives, setPdfCreatives] = React.useState<CreativeData[]>([]);
  const [downloading, setDownloading] = React.useState(false);
  // When the report data was last synced (UTC ISO from the delivery endpoint).
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState<string | null>(null);
  // Whether the page-level metrics fetch (which carries lastUpdatedAt) has
  // resolved, so the metrics gate shows a loading state rather than flashing the
  // "not ready" message before we know if a refresh has landed.
  const [metricsFetched, setMetricsFetched] = React.useState(false);
  // Advertiser branding (logo + primary color) auto-derived from their website,
  // used to theme the report to their own brand instead of the default green.
  // Seeded from the sessionStorage cache so a refresh applies the brand color
  // immediately (no green flash); the effect below revalidates.
  const [branding, setBranding] = React.useState<Branding>(() => readCachedBranding(submissionId));
  const [logoOk, setLogoOk] = React.useState(true);
  // Global date-range filter; null bounds = the full flight window.
  const [range, setRange] = React.useState<ReportRange>({ from: null, to: null });

  React.useEffect(() => {
    setSignInOpen(!isAuthenticated);
  }, [isAuthenticated]);

  React.useEffect(() => {
    const advertiserId = selectedAdvertiser?.advertiserId;
    if (!advertiserId || !submissionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // New submission → re-establish freshness from its own metrics fetch.
    setMetricsFetched(false);
    setLastUpdatedAt(null);
    // Same closed-loop scoping as the detail page: the URL pins the station,
    // so we ask the API to filter to one station, then pick the row.
    apiClient
      .post<{ rows: SelfServiceSubmissionRow[] }>('/submissions/list-by-advertiser', {
        advertiserId,
        stationSlug: slug,
      })
      .then(({ rows }) => {
        if (!cancelled) setRow((rows ?? []).find((r) => r.id === submissionId) ?? null);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load campaign');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAdvertiser?.advertiserId, submissionId, slug]);

  const { config: cfg } = usePublisherConfig(slug);
  // The report is themed to the advertiser's own brand: primary color
  // auto-derived from their website, falling back to the brand-agnostic green
  // when we couldn't derive a usable hex color.
  const accent =
    branding.primaryColor && /^#[0-9a-f]{6}$/i.test(branding.primaryColor)
      ? branding.primaryColor
      : REPORT_ACCENT;
  // A distinct brand secondary for the "completes" series (else the chart's gray).
  const completesColor =
    branding.secondaryColor && /^#[0-9a-f]{6}$/i.test(branding.secondaryColor)
      ? branding.secondaryColor
      : undefined;
  // Secondary for small page accents; falls back to the primary so two-tone
  // touches still render (as a subtle solid) when there's no distinct second color.
  const secondary = completesColor ?? accent;
  // CTV-658: a gated approved campaign (review gate moved it to the pending
  // bucket) that already delivered keeps its report readable.
  const reportShown =
    !!row &&
    (bucketFor(row) === 'completed' ||
      bucketFor(row) === 'active' ||
      (row.status === 'approved' && hasDeliveredData(row)));
  // Live = currently delivering (today is inside the flight window). Drives
  // present-tense copy and the pacing indicator.
  const live = !!row && bucketFor(row) === 'active';

  // A short label for the active date range ("Last 30 days" / "Apr 10 – Apr 20"),
  // shown next to each section title; null when viewing the full flight window.
  // The end is capped at today: a live campaign's flight runs into the future,
  // but there's no data past today, so the range filter shouldn't either.
  const campStart = row?.campaign?.startDate?.slice(0, 10) ?? null;
  const campEndRaw = row?.campaign?.endDate?.slice(0, 10) ?? null;
  const campEnd = campEndRaw ? minISO(campEndRaw, todayISO()) : null;
  const rangeBadge = campStart && campEnd ? rangeLabel(range, campStart, campEnd) : null;
  // "Last updated" stamp (UTC) for the report-freshness note.
  const lastUpdated = formatUtcStamp(lastUpdatedAt);
  // Reporting keeps reconciling for a few days after the end date (the cron's
  // BUFFER_DAYS grace + its final reconciliation pull), so the "may change"
  // disclaimer stays up through that window even after the campaign completes.
  const REPORT_SETTLING_DAYS = 3; // mirrors the cron: BUFFER_DAYS (2) + the finalize day
  const settling =
    reportShown && !live && !!campEndRaw && todayISO() <= addDaysISO(campEndRaw, REPORT_SETTLING_DAYS);

  // Metrics gate: hold all charts/metrics until the campaign's day-4 data pull has
  // actually landed. The daily cron pulls the day after delivery starts, so a
  // complete, accurate picture only exists once the day-4 pull runs (covering
  // through day 3). We don't ungate on the calendar alone — we require a metrics
  // write dated on/after day 4 (lastUpdatedAt = MAX(updated_at) across the metric
  // tables), so a missing/delayed/failed pull keeps the gate up. Until then we
  // show a "check back" message instead of charts.
  // TEMPORARY (pre-launch testing): bypass the day-4 metrics gate so we can watch
  // delivery come in as soon as the cron writes it. Toggle back on by unsetting
  // VITE_SELFSERVICE_METRICS_NO_DELAY (or setting it to anything but "true") on the
  // frontend static site and rebuilding. Remove this flag + the `|| metricsGateDisabled`
  // below when we're done testing.
  const metricsGateDisabled = import.meta.env.VITE_SELFSERVICE_METRICS_NO_DELAY === 'true';
  const METRICS_READY_DAY = 3; // days after start_date (day 1 = start) → available on day 4
  const metricsAvailableOn = campStart ? addDaysISO(campStart, METRICS_READY_DAY) : null;
  const metricsReady =
    metricsGateDisabled ||
    (!!metricsAvailableOn &&
      !!lastUpdatedAt &&
      new Date(lastUpdatedAt).getTime() >= new Date(`${metricsAvailableOn}T00:00:00Z`).getTime());

  // CTV-468: the report is "viewed" when this page surfaces it (the user
  // clicked through), not merely when the detail page loads.
  React.useEffect(() => {
    if (reportShown) {
      analytics.track(EVENTS.DELIVERY_REPORT_VIEWED, {
        submission_id: submissionId,
        station_slug: slug,
      });
    }
  }, [reportShown, submissionId, slug]);

  // Derive the advertiser's brand (logo + primary color) from their website.
  React.useEffect(() => {
    if (!reportShown || !row) return;
    let cancelled = false;
    setLogoOk(true);
    apiClient
      .get<{ logoUrl: string | null; primaryColor: string | null; secondaryColor: string | null }>(
        `/submissions/${row.id}/advertiser-branding${wantsFreshBranding ? '?refresh=1' : ''}`,
      )
      .then((b) => {
        const next = b ?? EMPTY_BRANDING;
        if (!cancelled) {
          setBranding(next);
          writeCachedBranding(submissionId, next);
        }
      })
      .catch(() => {
        // Keep the seeded/cached branding on a transient failure rather than
        // blanking to the default green.
      });
    return () => {
      cancelled = true;
    };
  }, [reportShown, row, wantsFreshBranding, submissionId]);

  // Load every section's data backing the PDF once the report is shown, scoped
  // to the active range. allSettled so a single failing endpoint still yields a
  // usable PDF from the rest.
  React.useEffect(() => {
    if (!reportShown || !row) return;
    let cancelled = false;
    const q = rangeQuery(range);
    Promise.allSettled([
      apiClient.get<{
        daily: Array<{ date: string; plays: number; completes?: number }>;
        flights?: Array<{ pacing_impressions: number }>;
        lastUpdatedAt?: string | null;
      }>(`/submissions/${row.id}/delivery-daily${q}`),
      apiClient.get<GeoData>(`/submissions/${row.id}/geo${q}`),
      apiClient.get<DaypartData>(`/submissions/${row.id}/daypart${q}`),
      apiClient.get<{ byCreative: CreativeData[] }>(`/submissions/${row.id}/by-creative${q}`),
    ]).then(([d, g, dp, c]) => {
      if (cancelled) return;
      if (d.status === 'fulfilled') {
        setPdfDaily(d.value.daily ?? []);
        setPdfFlights(d.value.flights ?? []);
        setLastUpdatedAt(d.value.lastUpdatedAt ?? null);
      } else {
        setPdfDaily([]);
        setPdfFlights([]);
      }
      setPdfGeo(g.status === 'fulfilled' ? g.value : null);
      setPdfDaypart(dp.status === 'fulfilled' ? dp.value : null);
      setPdfCreatives(c.status === 'fulfilled' ? c.value.byCreative ?? [] : []);
      setMetricsFetched(true);
    });
    return () => {
      cancelled = true;
    };
  }, [reportShown, row, range]);

  const handleDownloadPdf = async () => {
    if (!row || !cfg || pdfDaily.length === 0) return;
    setDownloading(true);
    try {
      // CTV-445: when a date-range filter is active, scope the whole PDF to it.
      // The section data was already fetched range-scoped (effect above); here we
      // narrow the header subtitle, the campaign date line, and the filename to
      // match. rangeBadge is non-null exactly when a filter (preset/custom) is on.
      const scope =
        rangeBadge && campStart && campEnd
          ? { label: rangeBadge, startDate: range.from ?? campStart, endDate: range.to ?? campEnd }
          : undefined;
      const monthly = aggregateMonthly(pdfDaily);
      const total = pdfDaily.reduce((a, d) => a + d.plays, 0);
      const avg = pdfDaily.length > 0 ? Math.round(total / pdfDaily.length) : 0;
      const pacing =
        pdfFlights.length > 0
          ? pdfFlights.reduce((acc, f) => acc + (f.pacing_impressions ?? 0), 0)
          : row.pacing_impressions ?? 0;
      const goalPct = pacing > 0 ? Math.min(999, Math.round((total / pacing) * 100)) : null;

      // Completion rate from the same daily source as the headline total.
      const totalCompletes = pdfDaily.reduce((a, d) => a + (d.completes ?? 0), 0);
      const completionRate = total > 0 ? Math.min(100, Math.round((totalCompletes / total) * 100)) : null;

      // Daypart: shift the UTC hours into the campaign's market zone, then bucket
      // (identical pipeline to the on-page chart so the PDF matches it exactly).
      const daypart = pdfDaypart
        ? {
            windows: bucketByDaypart(shiftHoursToLocal(pdfDaypart.byHour ?? [], pdfDaypart.offsetMinutes)).map((b) => ({
              label: b.label,
              hours: b.hours,
              impressions: b.impressions,
              completes: b.completes,
            })),
            timezoneLabel: pdfDaypart.timezoneLabel || 'UTC',
          }
        : undefined;

      // Boundaries for the top ZIPs (≥100 impressions) so the PDF can draw a ZIP
      // choropleth framed on the main delivery area. Fetched on demand (export
      // only); impressions merged in by ZCTA. Best-effort — null omits the map.
      let zipBoundaries: Array<{
        zcta: string;
        latitude: number;
        longitude: number;
        boundary: GeoJSON.Geometry | null;
        impressions: number;
      }> | null = null;
      const topZips = (pdfGeo?.byZip ?? []).filter((z) => z.impressions >= 100).slice(0, 20);
      if (topZips.length > 0) {
        try {
          const r = await apiClient.post<{
            zips: Array<{ zcta: string; latitude: number; longitude: number; boundary: GeoJSON.Geometry | null }>;
          }>('/geographic/zip-boundaries', { zctas: topZips.map((z) => z.zip) });
          const impByZcta = Object.fromEntries(topZips.map((z) => [z.zip, z.impressions]));
          zipBoundaries = (r.zips ?? []).map((b) => ({ ...b, impressions: impByZcta[b.zcta] ?? 0 }));
        } catch {
          zipBoundaries = null;
        }
      }

      // Mapbox static basemap for the ZIP map, sized to the same frame the PDF
      // projects onto (so it aligns under the polygons).
      const zipFrame = zipBoundaries ? computeZipMapFrame(zipBoundaries) : null;
      const zipBasemapUrl = zipFrame ? zipBasemapUrlBuilder(zipFrame, import.meta.env.VITE_MAPBOX_TOKEN) : null;

      const geo = pdfGeo
        ? {
            byState: (pdfGeo.byState ?? []).map((r) => ({ code: r.state, impressions: r.impressions, completes: r.completes })),
            byDma: (pdfGeo.byDma ?? []).map((r) => ({ code: r.dma, name: r.name ?? null, impressions: r.impressions, completes: r.completes })),
            byZip: (pdfGeo.byZip ?? []).map((r) => ({ code: r.zip, impressions: r.impressions, completes: r.completes })),
            zipBoundaries,
            zipBasemapUrl,
          }
        : undefined;

      // Mirror the on-page rule: the by-creative breakdown only renders for
      // multi-creative campaigns.
      const creatives =
        pdfCreatives.length > 1
          ? pdfCreatives.map((c) => ({ name: c.name, impressions: c.impressions, completes: c.completes }))
          : undefined;

      // Fetch the logo server-side as a data URL — the advertiser's cross-origin
      // logo can't be canvas-exported in the browser, but a data URL embeds
      // cleanly. Best-effort: omit the logo on any failure.
      let logoDataUrl: string | null = null;
      if (logoOk && branding.logoUrl) {
        try {
          const r = await apiClient.get<{ dataUrl: string | null }>(`/submissions/${row.id}/advertiser-logo`);
          logoDataUrl = r?.dataUrl ?? null;
        } catch {
          logoDataUrl = null;
        }
      }

      // Publisher (station) logo, fetched the same server-side way.
      let publisherLogoDataUrl: string | null = null;
      if (cfg.logoUrl) {
        try {
          const r = await apiClient.get<{ dataUrl: string | null }>(`/submissions/${row.id}/publisher-logo`);
          publisherLogoDataUrl = r?.dataUrl ?? null;
        } catch {
          publisherLogoDataUrl = null;
        }
      }

      // A windowed report shows partial delivery, so the lifetime "Goal %"
      // (window plays ÷ full pacing goal) would read as a misleadingly low
      // number — drop it when scoped.
      await exportDeliveryReportToPdf(
        { row, config: cfg, daily: pdfDaily, monthly, totalDelivered: total, dailyAvg: avg, goalPct: scope ? null : goalPct, completionRate, daypart, geo, creatives, scope, accentColor: accent, logoUrl: logoDataUrl, publisherLogoUrl: publisherLogoDataUrl, dataUpdatedLabel: formatUtcStamp(lastUpdatedAt) },
        deliveryReportFilenameFor(row, cfg, scope?.label),
      );
      analytics.track(EVENTS.DELIVERY_REPORT_DOWNLOADED, {
        station_slug: cfg.slug,
        submission_id: row.id,
        scope: scope ? 'range' : 'lifetime',
      });
    } finally {
      setDownloading(false);
    }
  };

  if (!submissionId) return <Navigate to={`/${slug}/my-campaigns`} replace />;

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Sign in to view this report</h1>
          <button
            type="button"
            onClick={() => setSignInOpen(true)}
            className="mt-4 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
          >
            Sign in
          </button>
        </div>
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SharedKeyframes />
      <PublicHeader
        publisher={cfg ?? undefined}
        rightSlot={
          <Link
            to={`/${slug}/my-campaigns/${submissionId}`}
            className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500 hover:text-slate-900 px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to campaign
          </Link>
        }
      />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {loading ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading report…
          </div>
        ) : error ? (
          <Banner tone="warn" label="Couldn't load this report">{error}</Banner>
        ) : !row ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <AlertCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-700 font-medium">Campaign not found</p>
            <p className="text-sm text-slate-500 mt-1">It may have been removed, or you may be signed in with a different email.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Report header — title/info on the left, the advertiser logo
                centered on the page (equal-width outer columns keep it centered),
                Download PDF on the right, all on one row. */}
            <div className="px-1 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: accent }}>
                  <BarChart3 className="w-3.5 h-3.5" />
                  Performance report
                </div>
                <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl md:text-[26px] font-bold text-slate-900 tracking-tight leading-tight">
                    {row.campaign?.campaignName?.trim() || 'Campaign'}
                  </h1>
                  {(() => {
                    const live = bucketFor(row) === 'active';
                    return (
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                          live ? '' : 'bg-slate-100 text-slate-600'
                        }`}
                        style={live ? { backgroundColor: `${accent}1a`, color: accent } : undefined}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: live ? accent : '#94a3b8' }}
                        />
                        {live ? 'Delivering' : 'Completed'}
                      </span>
                    );
                  })()}
                </div>
                <div className="mt-1 text-[13px] text-slate-500 space-y-0.5">
                  {cfg?.name && <div>{cfg.name}</div>}
                  {row.campaign?.startDate && row.campaign?.endDate && (
                    <div>
                      {formatDate(row.campaign.startDate)} – {formatDate(row.campaign.endDate)}
                    </div>
                  )}
                </div>
                {/* Two-tone brand accent under the header. */}
                <div
                  className="mt-3 h-[3px] w-14 rounded-full"
                  style={{ background: `linear-gradient(90deg, ${accent}, ${secondary})` }}
                />
              </div>
              <div className="justify-self-center">
                {branding.logoUrl && logoOk && (
                  <img
                    src={branding.logoUrl}
                    alt={`${row.business_info?.businessName ?? 'Advertiser'} logo`}
                    className="h-24 max-w-[320px] object-contain"
                    onError={() => setLogoOk(false)}
                  />
                )}
              </div>
              <div className="justify-self-end flex flex-col items-end gap-2">
                {reportShown && metricsReady && pdfDaily.length > 0 && (
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={downloading}
                    className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold text-white shadow-sm transition-colors disabled:opacity-60 disabled:cursor-wait"
                    style={{ backgroundColor: accent }}
                    aria-label="Download performance report PDF"
                  >
                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    {downloading ? 'Generating…' : rangeBadge ? 'Download Filtered PDF' : 'Download PDF'}
                  </button>
                )}
                {/* CTV-445: when a date filter is active, denote that the export
                    is scoped to it (names the window so it's unambiguous). */}
                {reportShown && pdfDaily.length > 0 && rangeBadge && (
                  <div
                    className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em]"
                    style={{ color: accent }}
                  >
                    <Filter className="w-3 h-3" aria-hidden />
                    Filtered · {rangeBadge}
                  </div>
                )}
                {/* Freshness note under the button: the "Updated …" stamp, a
                    daily-refresh reminder while live, and the "data settles"
                    disclaimer while live OR within the post-end settling window
                    (the cron reconciles for a few days after the end date). */}
                {metricsReady && (live || settling || lastUpdated) && (
                  <div className="text-[11px] leading-relaxed text-slate-400 text-right">
                    {lastUpdated && <div>Data updated {lastUpdated}.</div>}
                    {live && <div>Refreshes once a day, around 12:00&nbsp;PM&nbsp;UTC.</div>}
                    {(live || settling) && <div>Recent daily numbers may change as data settles.</div>}
                  </div>
                )}
              </div>
            </div>

            {!(reportShown && cfg) ? (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
                <BarChart3 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-700 font-medium">Performance reporting isn’t available yet</p>
                <p className="text-sm text-slate-500 mt-1">
                  Metrics appear here once your campaign starts delivering.
                </p>
              </div>
            ) : !metricsFetched ? (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading report…
              </div>
            ) : !metricsReady ? (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
                <CalendarClock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-700 font-medium">
                  Campaign metrics will be available starting on day 4 of the campaign to ensure accurate data is captured.
                </p>
                {metricsAvailableOn && (
                  <p className="text-sm text-slate-500 mt-1">Check back on {formatDate(metricsAvailableOn)}.</p>
                )}
              </div>
            ) : (
              <>
                <InsightsBand submissionId={row.id} accentColor={accent} from={range.from} to={range.to} live={live} />
                <CampaignStatsBand row={row} accentColor={accent} secondaryColor={secondary} from={range.from} to={range.to} rangeLabel={rangeBadge} live={live} />
                {campStart && campEnd && (
                  <DateRangeFilter
                    start={campStart}
                    end={campEnd}
                    value={range}
                    onChange={setRange}
                    accentColor={accent}
                  />
                )}
                {/* Plays by week — full width. */}
                <PerformanceTrends
                  variant="week"
                  submissionId={row.id}
                  accentColor={accent}
                  secondaryColor={secondary}
                  from={range.from}
                  to={range.to}
                  rangeLabel={rangeBadge}
                />
                <GeographicSection
                  submissionId={row.id}
                  accentColor={accent}
                  secondaryColor={secondary}
                  from={range.from}
                  to={range.to}
                  rangeLabel={rangeBadge}
                />
                {/* Plays by day of week + daypart, side by side. Taller on wide
                    screens so the 8-row daypart card isn't cramped (both stretch
                    to match). */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch lg:[&>*]:min-h-[27rem]">
                  <PerformanceTrends
                    variant="dow"
                    submissionId={row.id}
                    accentColor={accent}
                    secondaryColor={secondary}
                    from={range.from}
                    to={range.to}
                    rangeLabel={rangeBadge}
                  />
                  <DaypartChart
                    submissionId={row.id}
                    accentColor={accent}
                    secondaryColor={secondary}
                    from={range.from}
                    to={range.to}
                    rangeLabel={rangeBadge}
                  />
                </div>
                <ByCreativeSection submissionId={row.id} slug={cfg.slug} accentColor={accent} secondaryColor={secondary} from={range.from} to={range.to} rangeLabel={rangeBadge} />
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default CampaignReportPage;
